import { useEffect, useRef } from 'react';

import Text from '@cartwhl/ui/base/TextNew';

import NavigationLink from '../../NavigationLink';
import type { NavItem, NavMenuSection } from '../content';
import './DesktopMenu.css';

interface DesktopMenuProps {
  label: string;
  items?: readonly NavItem[];
  sections?: readonly NavMenuSection[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface MenuItemProps {
  item: NavItem;
  onNavigate: () => void;
}

const menuItemContentClass = 'block rounded-lg px-4 py-[11px]';

function MenuItem({ item, onNavigate }: MenuItemProps) {
  const content = (
    <>
      <Text
        token="14/medium"
        className="cw-text-box-none block leading-[18px]"
      >
        {item.label}
      </Text>
      {item.subtitle && (
        <Text
          as="span"
          token="14/medium"
          className="cw-text-box-none text-tertiary block leading-[18px]"
        >
          {item.subtitle}
        </Text>
      )}
    </>
  );

  if ('unavailable' in item) {
    return (
      <span
        className={`text-primary cursor-not-allowed ${menuItemContentClass}`}
        aria-disabled="true"
        title={item.unavailable}
      >
        {content}
        <span className="sr-only">{item.unavailable}</span>
      </span>
    );
  }
  return (
    <NavigationLink
      href={item.href}
      external={item.external}
      onNavigate={onNavigate}
      className={`hover:bg-secondary dark:hover:bg-primary text-primary ${menuItemContentClass}`}
    >
      {content}
    </NavigationLink>
  );
}

interface MenuSectionsProps {
  sections: readonly NavMenuSection[];
  onNavigate: () => void;
}

function MenuSections({
  sections,
  onNavigate,
}: MenuSectionsProps) {
  return (
    <div
      className="grid items-start"
      style={{ gridTemplateColumns: `repeat(${sections.length}, 224px)` }}
    >
      {sections.map((section) => (
        <section className="w-[224px] py-2" key={section.id}>
          <div className="px-2">
            <Text
              as="h2"
              token="14/medium"
              className="border-primary text-tertiary block h-10 border-b px-4 py-[11px] leading-[18px]"
            >
              {section.label}
            </Text>
          </div>
          {section.items.map((item) => (
            <div className="px-2" key={item.id}>
              <MenuItem item={item} onNavigate={onNavigate} />
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}

export default function DesktopMenu({
  label,
  items,
  sections,
  open,
  onOpenChange,
}: DesktopMenuProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const hoverSuppressedRef = useRef(false);
  const openRef = useRef(open);
  openRef.current = open;

  function closeMenu({ restoreFocus = false } = {}) {
    onOpenChange(false);
    if (restoreFocus) triggerRef.current?.focus();
  }

  function openFromHover() {
    if (hoverSuppressedRef.current) return;
    onOpenChange(true);
  }

  function openFromTrigger() {
    hoverSuppressedRef.current = false;
    onOpenChange(true);
  }

  useEffect(() => {
    function onDocumentPointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        onOpenChange(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && openRef.current) {
        hoverSuppressedRef.current = true;
        onOpenChange(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('mousedown', onDocumentPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocumentPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onOpenChange]);

  function closeAfterFocusExit() {
    window.setTimeout(() => {
      if (!rootRef.current?.contains(document.activeElement)) closeMenu();
    }, 0);
  }

  return (
    <div
      ref={rootRef}
      className="relative"
      onMouseEnter={openFromHover}
      onMouseLeave={() => {
        hoverSuppressedRef.current = false;
        closeMenu();
      }}
      onBlur={closeAfterFocusExit}
    >
      <button
        ref={triggerRef}
        type="button"
        className="text-primary hover:text-secondary flex items-center gap-1"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={openFromTrigger}
      >
        <Text token="16/medium">{label}</Text>
      </button>
      {/* Keep the submenu mounted so its exit transition can finish. */}
      <div
        className={`desktop-menu-popover absolute left-[-24px] top-full z-20 pt-3 ${
          sections ? 'min-w-0' : 'min-w-56'
        } ${open ? 'desktop-menu-popover-open' : ''}`}
        aria-hidden={!open}
        style={{
          // Include the content border so fixed-width columns don't create 2px of overflow.
          width: sections ? sections.length * 224 + 2 : undefined,
        }}
      >
        <div
          className="desktop-menu-content cw-text-box-none bg-primary dark:bg-secondary dark:border-secondary rounded-2xl border border-transparent"
        >
          <div
            className={`no-scrollbar max-h-[calc(100vh-6rem)] overflow-x-auto overflow-y-auto rounded-2xl ${
              sections ? '' : 'py-2'
            }`}
          >
            {sections ? (
              <MenuSections sections={sections} onNavigate={closeMenu} />
            ) : (
              items?.map((item) => (
                <div className="px-2" key={item.id}>
                  <MenuItem item={item} onNavigate={closeMenu} />
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
